// Canned-response (template) client hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface CannedTemplate {
  id: number;
  name: string;
  bodyHtml: string;
  updatedAt: string;
}

export function useTemplates() {
  return useQuery<{ templates: CannedTemplate[] }>({
    queryKey: ['templates'],
    queryFn: async () => {
      const r = await fetch('/api/templates', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

function useInvalidateTemplates() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['templates'] });
}

async function jsend(url: string, method: string, body?: unknown): Promise<Response> {
  const r = await fetch(url, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as { error?: string }).error || `HTTP ${r.status}`);
  }
  return r;
}

export function useCreateTemplate() {
  const inval = useInvalidateTemplates();
  return useMutation({
    mutationFn: (v: { name: string; bodyHtml: string }) => jsend('/api/templates', 'POST', v),
    onSuccess: inval,
  });
}

export function useUpdateTemplate() {
  const inval = useInvalidateTemplates();
  return useMutation({
    mutationFn: (v: { id: number; name?: string; bodyHtml?: string }) =>
      jsend(`/api/templates/${v.id}`, 'PUT', { name: v.name, bodyHtml: v.bodyHtml }),
    onSuccess: inval,
  });
}

export function useDeleteTemplate() {
  const inval = useInvalidateTemplates();
  return useMutation({
    mutationFn: (id: number) => jsend(`/api/templates/${id}`, 'DELETE'),
    onSuccess: inval,
  });
}
